/** WP Recipe Maker nutrition, presented for humans.
 *
 *  WPRM stores nutrition as snake_case keys with bare numbers. This module gives each field its
 *  customer-facing label and unit, and prefers a unit supplied by WPRM (either a `<field>_unit` key
 *  or a unit already written into the value) over the default below.
 */

const FIELDS = {
  calories: { label: "Calories", unit: "kcal" },
  carbohydrates: { label: "Carbohydrates", unit: "g" },
  protein: { label: "Protein", unit: "g" },
  fat: { label: "Fat", unit: "g" },
  saturated_fat: { label: "Saturated Fat", unit: "g" },
  polyunsaturated_fat: { label: "Polyunsaturated Fat", unit: "g" },
  monounsaturated_fat: { label: "Monounsaturated Fat", unit: "g" },
  trans_fat: { label: "Trans Fat", unit: "g" },
  cholesterol: { label: "Cholesterol", unit: "mg" },
  sodium: { label: "Sodium", unit: "mg" },
  potassium: { label: "Potassium", unit: "mg" },
  fiber: { label: "Fiber", unit: "g" },
  sugar: { label: "Sugar", unit: "g" },
  vitamin_a: { label: "Vitamin A", unit: "µg" },
  vitamin_c: { label: "Vitamin C", unit: "mg" },
  calcium: { label: "Calcium", unit: "mg" },
  iron: { label: "Iron", unit: "mg" },
  serving_size: { label: "Serving Size", unit: "" },
};

/** WPRM publishes recipe times as bare minutes ("20") on some recipes and as text ("10 mins") on
 *  others. Readers should always see a unit: 20 → "20 mins", 60 → "1 hr", 90 → "1 hr 30 mins".
 *  Anything WPRM already wrote with words is left exactly as published. */
export const formatDuration = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text || /[a-z]/i.test(text)) return text || null;
  const minutes = Number(text);
  if (!Number.isFinite(minutes) || minutes <= 0) return text;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} mins`;
  return rest ? `${hours} hr ${rest} mins` : `${hours} hr`;
};

/** Anything not in the table still reads properly: `some_new_field` → `Some New Field`. */
const titleCase = (key) =>
  key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value).trim();
  // Keep the precision WPRM published (0.4 g stays 0.4), only group the thousands.
  const decimals = String(value).includes(".") ? String(value).split(".")[1].replace(/0+$/, "").length : 0;
  return number.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/** [{ key, label, value }] — ready to render, never a database key, never a bare number. */
export const nutritionRows = (nutrition) => {
  if (!nutrition) return [];
  const rows = [];
  for (const [key, raw] of Object.entries(nutrition)) {
    if (key.endsWith("_unit") || raw === null || raw === undefined || String(raw).trim() === "") continue;
    const field = FIELDS[key];
    const text = String(raw).trim();
    // A value WPRM already wrote with its unit ("118 kcal") is left as published.
    const match = text.match(/^(-?[\d.,]+)\s*(.*)$/);
    const amount = match ? formatNumber(match[1].replace(/,/g, "")) : text;
    const unit = (match && match[2]) || nutrition[`${key}_unit`] || field?.unit || "";
    rows.push({
      key,
      label: field?.label ?? titleCase(key),
      value: unit ? `${amount} ${unit}` : amount,
    });
  }
  // Show the fields readers look for first, then anything else in WPRM's own order.
  const order = Object.keys(FIELDS);
  return rows.sort((a, b) => {
    const ai = order.indexOf(a.key);
    const bi = order.indexOf(b.key);
    return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
  });
};
